import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

function NavItem({ name, url, onClick }) {
  return (
    <a
      href={url}
      onClick={onClick}
      className="transition duration-300 hover:text-[#895389]"
    >
      {name}
    </a>
  );
}

function Nav() {
  const [open, setOpen] = useState(false);

  // Fermer avec Échap + bloquer le scroll quand le menu est ouvert
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    document.body.classList.toggle("overflow-hidden", open);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.classList.remove("overflow-hidden");
    };
  }, [open]);

  // Liens de navigation (réutilisés Desktop/Mobile)
  const links = [
    { name: "Home", url: "#" },
    { name: "Projects", url: "#projects" },
    { name: "Certificates", url: "#certificates" },
    { name: "Contact", url: "#contact" },
  ];

  return (
    <>
      {/* ------- Desktop / Laptop (≥ 900px) : barre top fixe existante ------- */}
      <motion.div
        className="hidden min-[900px]:flex fixed z-30 justify-between items-center bg-[#ffffff0f] backdrop-blur-md w-[70%] mt-4 mx-[16%] p-2 text-[#ECECEC] font-bold text-xl rounded-[2.2rem]"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <motion.p
          className="ml-2 text-[1.6rem] text-[#F1F1F1] fontFamily-Inter"
          initial={{ x: -1000 }}
          animate={{ x: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <span className="text-[#895389] text-[20px]">I.S</span>
        </motion.p>

        <div className="flex justify-center items-center gap-9">
          {links.slice(0, 3).map((l) => (
            <NavItem key={l.name} name={l.name} url={l.url} />
          ))}
          <NavItem name="contact" url="#contact" />
        </div>

        <motion.a
          href="#contact"
          className="px-8 py-3 rounded-3xl bg-[#895389] transition duration-300 hover:bg-[#ffffff1a]"
          initial={{ x: 1000 }}
          animate={{ x: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          Contact me
        </motion.a>
      </motion.div>

      {/* ------- Mobile / Tablet (< 900px) : header + bouton hamburger ------- */}
      <div className="min-[900px]:hidden fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-4 py-3 bg-[#ffffff0f] backdrop-blur-md text-[#ECECEC]">
        <span className="text-[1.6rem] text-[#F1F1F1]">
          {/* <span className="text-[#895389] text-[20px]">I.S</span> */}
        </span>

        <button
          type="button"
          aria-label="Ouvrir le menu"
          aria-controls="mobile-sidebar"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className="p-2 rounded-xl hover:bg-white/10 active:scale-[0.98] transition"
        >
          {/* Icône hamburger */}
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* ------- Sidebar Drawer + Overlay (Mobile) ------- */}
      <AnimatePresence>
        {open && (
          <>
            {/* Overlay */}
            <motion.div
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] min-[900px]:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              aria-hidden="true"
              onClick={() => setOpen(false)}
            />

            {/* Sidebar */}
            <motion.aside
              id="mobile-sidebar"
              role="dialog"
              aria-modal="true"
              className="fixed top-0 left-0 bottom-0 z-50 w-[78%] max-w-[320px] bg-[#0c0d11]/95 border-r border-white/10 text-[#ECECEC] min-[900px]:hidden flex flex-col"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 380, damping: 38 }}
            >
              {/* Header du panneau */}
              <div className="flex items-center justify-between px-4 py-4">
                <span className="text-[1.6rem] text-[#F1F1F1]">
                  {/* <span className="text-[#895389] text-[20px]">I.S</span> */}
                </span>
                <button
                  type="button"
                  aria-label="Fermer le menu"
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-xl hover:bg-white/10 transition"
                >
                  {/* Icône close */}
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Liens */}
              <nav className="mt-2 px-3">
                {links.map((l) => (
                  <a
                    key={l.name}
                    href={l.url}
                    onClick={() => setOpen(false)}
                    className="block text-lg font-semibold px-3 py-3 rounded-xl hover:text-[#895389] hover:bg-white/5 transition"
                  >
                    {l.name}
                  </a>
                ))}
              </nav>

              {/* CTA en bas */}
              <div className="mt-auto p-4">
                <a
                  href="#contact"
                  onClick={() => setOpen(false)}
                  className="block text-center px-6 py-3 rounded-2xl bg-[#895389] font-semibold hover:bg-[#ffffff1a] transition"
                >
                  Contact me
                </a>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default Nav;
